<template>
  <v-container class="about-yourself-container standard-content-container">
    <v-row no-gutters>
      <v-col cols="12" md="5" class="form-blurb">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link
              :to="{ name: 'OverviewPage', hash: '#about-yourself' }"
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1>Tell us who you are</h1>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="7">
        <div class="left-section-container">
          <div v-if="!loading">
            <ValidationObserver ref="observer">
              <v-form @submit.prevent="submit">
                <v-row>
                  <v-col cols="6" class="pb-0">
                    <ValidationProvider
                      id="first_names"
                      v-slot="{ errors }"
                      name="first_names"
                      rules="required"
                    >
                      <TextField
                        v-model.trim="testator.firstNames"
                        :label="'First names*'"
                        :errors="errors"
                        :name="'first_names'"
                      />
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="6" class="pb-0">
                    <ValidationProvider
                      id="last_name"
                      v-slot="{ errors }"
                      name="last_name"
                      rules="required"
                    >
                      <TextField
                        v-model.trim="testator.lastName"
                        :label="'Last name*'"
                        :errors="errors"
                        :name="'last_name'"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>

                <h5 class="text-subtitle-1 mt-3 mb-0">
                  <strong>Identity number details</strong>
                </h5>
                <ValidationProvider
                  id="id_type"
                  v-slot="{ errors }"
                  name="id_type"
                  rules="required"
                >
                  <RadioSelect
                    v-model="identityNumber.idType"
                    :disabled="disableIdNumber"
                    :items="[
                      { value: 'ID', text: 'National ID' },
                      { value: 'PASSPORT', text: 'Passport' },
                    ]"
                    :color="'white'"
                    :errors="errors"
                    :group="'id_type'"
                  />
                </ValidationProvider>

                <ValidationProvider
                  v-if="identityNumber.idType"
                  id="country_code"
                  v-slot="{ errors }"
                  name="country_code"
                  rules="required"
                >
                  <ListSelect
                    :disabled="disableIdNumber"
                    v-model="identityNumber.countryCode"
                    :items="$options.countryDropdownOptions"
                    :group="'country_code'"
                    :name="'country_code'"
                    :label="'Nationality*'"
                    :errors="errors"
                  />
                </ValidationProvider>

                <ValidationProvider
                  v-if="identityNumber.idType"
                  id="id_or_passport_number"
                  v-slot="{ errors }"
                  name="id_or_passport_number"
                  :rules="`${
                    isSouthAfricanID
                      ? 'required|id_number|age_is_valid'
                      : 'required'
                  }`"
                >
                  <TextField
                    v-model.trim="identityNumber.number"
                    :label="idOrPassportNumberLabel"
                    :errors="errors"
                    :disabled="disableIdNumber"
                    :name="'id_or_passport_number'"
                  />
                </ValidationProvider>

                <v-menu
                  v-if="
                    identityNumber.idType &&
                    identityNumber.countryCode &&
                    !isSouthAfricanID
                  "
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template #activator="{ on }">
                    <ValidationProvider
                      id="date_of_birth"
                      v-slot="{ errors }"
                      name="date_of_birth"
                      rules="required"
                      mode="lazy"
                    >
                      <v-text-field
                        v-model="testator.dateOfBirth"
                        :error-messages="errors"
                        label="Date of Birth*"
                        :prepend-icon="icons.mdiCalendar"
                        readonly
                        v-on="on"
                      />
                    </ValidationProvider>
                  </template>
                  <v-date-picker
                    ref="dateMenu"
                    v-model="testator.dateOfBirth"
                    :max="currentDate"
                    @input="dateMenu = false"
                  />
                </v-menu>

                <ValidationProvider
                  v-if="showEmailField"
                  id="email"
                  v-slot="{ errors }"
                  name="email"
                  :rules="isMigratedFromTeba ? 'email' : 'required|email'"
                >
                  <TextField
                    v-model.trim="testator.email"
                    :label="isMigratedFromTeba ? 'Email' : 'Email*'"
                    :name="'email'"
                    :errors="errors"
                  />
                </ValidationProvider>

                <ValidationProvider
                  id="cell_number"
                  v-slot="{ errors }"
                  name="cell_number"
                  :rules="{
                    required: true,
                    phoneIntl: [testator.cellCountryCode],
                  }"
                >
                  <TextField
                    v-model.trim="testator.cellNumber"
                    :label="'Cell number*'"
                    :errors="errors"
                    :name="'cell_number'"
                    :isCountryCode="true"
                  >
                    <template #prepend>
                      <vue-tel-input
                        :preferred-countries="['ZA', 'GB']"
                        :default-country="testator.cellCountryCode"
                        @country-changed="countrySelected"
                      >
                        <template #arrow-icon>
                          <strong>{{ countryDialCode }}</strong>
                        </template>
                      </vue-tel-input>
                    </template>
                  </TextField>
                </ValidationProvider>

                <ValidationProvider
                  id="gender"
                  v-slot="{ errors }"
                  name="gender"
                  rules="required"
                >
                  <RadioSelect
                    v-model="testator.gender"
                    :items="[
                      { value: 'MALE', text: 'Male' },
                      { value: 'FEMALE', text: 'Female' },
                    ]"
                    :color="'white'"
                    :errors="errors"
                    :group="'gender'"
                  />
                </ValidationProvider>

                <div class="button-container">
                  <GradientButton
                    :title="'Save And Continue'"
                    :small="true"
                    :type="'submit'"
                    :callout="'right'"
                    :loading="buttonLoading"
                  />
                </div>
              </v-form>
            </ValidationObserver>
          </div>

          <div v-else class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </div>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import {
    TESTATOR_QUERY,
    UPDATE_TESTATOR_MUTATION,
  } from '../../constants/graphql/testators';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { parsePhoneNumber } from 'awesome-phonenumber';
  import { countryDropdownOptions } from '../../constants/js/countries';

  export default {
    name: 'AboutYourself',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    data() {
      return {
        testator: {},
        identityNumber: {},
        loading: true,
        countryDialCode: null,
        dateMenu: false,
        showEmailField: false,
      };
    },
    computed: {
      isAdminLoggedInAsSomeoneElse() {
        return this.$store.getters.isAdminLoggedInAsSomeoneElse;
      },
      cellNumberSignificant() {
        const pn = parsePhoneNumber(this.testator.cellNumber, {
          regionCode: this.testator.cellCountryCode,
        });
        return pn.valid ? pn.number.significant : this.testator.cellNumber;
      },
      idOrPassportNumberLabel() {
        if (this.identityNumber.idType == 'PASSPORT') {
          return 'Passport number*';
        } else if (this.identityNumber.idType == 'ID') {
          return 'National ID number*';
        } else {
          return 'ID or passport number*';
        }
      },
      currentDate() {
        return new Date().toJSON().slice(0, 10);
      },
      disableIdNumber() {
        if (this.isAdminLoggedInAsSomeoneElse && !this.testator.insurance) {
          return false;
        }
        return (
          this.testator.identityNumber &&
          this.testator.identityNumber.idType == 'ID'
        );
      },
      isSouthAfricanID() {
        return (
          this.identityNumber.idType == 'ID' &&
          this.identityNumber.countryCode == 'ZA'
        );
      },
      isMigratedFromTeba() {
        return !!this.testator?.user?.migratedFromTeba;
      },
    },
    countryDropdownOptions: countryDropdownOptions,
    watch: {
      dateMenu(value) {
        value &&
          this.$nextTick(() => (this.$refs.dateMenu.activePicker = 'YEAR'));
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        result(response) {
          if (response.data) {
            this.loading = false;
            if (this.testator.cellNumber && this.testator.cellCountryCode) {
              this.testator.cellNumber = this.cellNumberSignificant;
            }
            if (this.testator.identityNumber) {
              this.identityNumber = JSON.parse(
                JSON.stringify(this.testator.identityNumber)
              );
            }
            // Only the Old Mutual SSO flow produces a testator without an
            // email — capture it here for that brand only.
            if (!this.testator.email && this.whitelabel.brand === 'oldmutual') {
              this.showEmailField = true;
            }
          }
        },
      },
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateTestator();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      updateTestator() {
        const testatorInputDetails = this.testatorInputDetails();
        const id = this.testatorId;

        this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
            update: (cache, { data: { updateTestator } }) => {
              this.updateCacheAfterUpdateTestator(cache, updateTestator);
            },
          })
          .then(data => {
            this.redirectToNext();
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      testatorInputDetails() {
        var details = {
          firstNames: this.testator.firstNames,
          lastName: this.testator.lastName,
          cellNumber: this.countryDialCode + this.cellNumberSignificant,
          cellCountryCode: this.testator.cellCountryCode,
          gender: this.testator.gender,
        };

        details.identityNumber = {
          countryCode: this.identityNumber.countryCode,
        };

        // Only add identityNumber to payload if an id number didn't previously exist for the testator
        if (!this.disableIdNumber || this.isAdminLoggedInAsSomeoneElse) {
          details.identityNumber = {
            number: this.identityNumber.number,
            idType: this.identityNumber.idType,
            countryCode: this.identityNumber.countryCode,
          };
        }
        if (this.showEmailField && this.testator.email) {
          details.email = this.testator.email;
        }
        if (!this.isSouthAfricanID)
          details.dateOfBirth = this.testator.dateOfBirth;

        return details;
      },
      updateCacheAfterUpdateTestator(cache, updateTestator) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.firstNames = updateTestator.firstNames;
        data.testator.lastName = updateTestator.lastName;
        data.identityNumber = updateTestator.identityNumber;
        data.cellNumber = updateTestator.cellNumber;
        data.cellCountryCode = updateTestator.cellCountryCode;
        data.gender = updateTestator.gender;

        cache.writeQuery({ ...query, data: data });
      },
      redirectBack() {
        this.$router.back();
      },
      redirectToNext() {
        this.$router.push({ name: 'OverviewPage', hash: '#about-yourself' });
        this.buttonLoading = false;
      },
      countrySelected(val) {
        this.testator.cellCountryCode = val.iso2;
        this.countryDialCode = '+' + val.dialCode.toString();
      },
    },
  };
</script>
<style lang="scss">
  .about-yourself-container {
    input[name='telephone'] {
      display: none;
    }
  }
</style>

<style lang="scss" scoped>
  .about-yourself-container {
    padding-top: 40px !important;

    .header-row {
      padding-bottom: 40px;
      display: flex;
      gap: 76px;
      h1 {
        flex: 1;
        line-height: 38px !important;
      }
    }
    .submit-button {
      float: left;
      margin-top: 30px;
    }

    input[name='telephone'] {
      display: none;
    }
    .button-container {
      margin-top: 40px;
      display: flex;
      gap: 20px;
    }

    .vti__dropdown {
      color: rgba(0, 0, 0, 0.87) !important;
    }
  }
</style>
